import React, { Component } from 'react';

import type { DeliveryMapProps } from 'src/delivery/components/map';

// ECOMM-13538: original map.jsx (GMap) implementation, extracted verbatim. See ./map.tsx for
// the flag dispatcher; ECOMM-13539 deletes this file once Mapbox is fully rolled out.
export default class GoogleDeliveryMap extends Component<DeliveryMapProps, { zoom: number | undefined }> {
  // Not assigned until componentDidMount (after window.google is confirmed loaded).
  private map: google.maps.Map | undefined;

  private marker: google.maps.Marker | undefined;

  private infoWindow: google.maps.InfoWindow | undefined;

  // Replaces the original's deprecated string ref (ref='mapCanvas' + this.refs.mapCanvas) with
  // an equivalent object ref -- same DOM node, same lifecycle timing, no behavior change.
  private readonly canvasRef = React.createRef<HTMLDivElement>();

  constructor(props: DeliveryMapProps) {
    super(props);
    this.state = { zoom: 15 };
  }

  render(): JSX.Element | null {
    // @types/google.maps assumes this global is always present; it's script-tag-loaded and
    // may not be ready yet, so this check is real despite the no-unnecessary-condition warning.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.google) {
      return null;
    }
    const { height, width } = this.props;
    return (
      <div className='GMap'>
        <div className='GMap-canvas' data-testid='delivery-map-canvas' ref={this.canvasRef} style={{ height, width }} />
      </div>
    );
  }

  componentDidMount(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see render()
    if (!window.google) {
      return;
    }
    // Map/marker/infoWindow are created post-render because they manipulate the DOM directly.
    this.map = this.createMap();
    this.marker = this.createMarker();
    this.infoWindow = this.createInfoWindow();

    // Listeners can only be added once the map instance exists.
    window.google.maps.event.addListener(this.map, 'zoom_changed', () => this.handleZoomChange());
    this.infoWindow.open(this.map, this.marker);
  }

  componentWillUnmount(): void {
    // Disclosed hardening: the original called clearListeners unconditionally, but `this.map`
    // is honestly optional here (never set if window.google wasn't ready at mount). Guarding
    // avoids passing undefined to a function typed to require a real instance.
    if (this.map) {
      window.google.maps.event.clearListeners(this.map, 'zoom_changed');
    }
  }

  createMap(): google.maps.Map {
    if (!this.canvasRef.current) {
      // React attaches refs before componentDidMount runs; this is unreachable in practice.
      throw new TypeError('Expected the map canvas ref to be attached');
    }
    const mapOptions = { zoom: this.state.zoom, center: this.mapCenter() };
    const map = new window.google.maps.Map(this.canvasRef.current, mapOptions);
    window.google.maps.event.trigger(map, 'resize');
    return map;
  }

  mapCenter(): google.maps.LatLng {
    const { initialCenter } = this.props;
    // Disclosed: the original passed lat/lng straight through unguarded, so a missing value's
    // real external-library behavior is unverifiable here; Number(...) makes that coercion
    // explicit rather than silently casting a possibly-nullable value to number.
    return new window.google.maps.LatLng(Number(initialCenter.lat), Number(initialCenter.lng));
  }

  createMarker(): google.maps.Marker {
    return new window.google.maps.Marker({ position: this.mapCenter(), map: this.map });
  }

  createInfoWindow(): google.maps.InfoWindow {
    // The legacy { map, anchor } constructor options aren't in this version of @types/google.maps;
    // construct()+open() is the documented modern equivalent for the same visible result.
    return new window.google.maps.InfoWindow({ content: this.props.tag });
  }

  handleZoomChange(): void {
    if (!this.map) {
      // Only ever fires as a listener bound to this.map itself; unreachable in practice.
      throw new TypeError('Expected the map to already be created');
    }
    this.setState({ zoom: this.map.getZoom() });
  }
}
